import Mustache from 'mustache'
import { MustacheTemplate } from './CustomerCommunicationTemplates/CustomerCommunicationTemplates'
import { Guid } from './common-schemas'

export interface Person {
  id: Guid

  firstName: string
  lastName: string

  deactivatedAt?: string
}

export interface PersonWithAccountGuid extends Person {
  accountContacts: {
    accountGuid: Guid
  }[]
}

export type AvatarData = {
  avatarAltShortString: string
  avatarAltColor?: string
  avatarImgSrc?: string
  deactivatedAt?: string
  userGuid: string
}

export interface UserDisplayNameContainer {
  readonly userDisplayName: string
}

export interface RenderablePerson {
  displayName: string
  avatarData: AvatarData
}

export type PersonViewModel = Person & RenderablePerson

export const formatName = (name?: Pick<Person, 'firstName' | 'lastName'>) =>
  name ? `${name.firstName} ${name.lastName}` : ''

export type AccountNameTemplateContext = {
  firstName: string
  lastName: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zip?: string
}

export const DEFAULT_ACCOUNT_NAME_TEMPLATE = '{{firstName}} {{lastName}} - {{addressLine1}}' as MustacheTemplate

export type TemplateValidationResult = {
  valid: boolean
  errors: string[]
}

export const formatAccountDisplayNameWithTemplate = (
  template: MustacheTemplate,
  context: AccountNameTemplateContext,
): string => {
  // Temporarily disable HTML escaping for plain text template rendering
  // Account display names are plain text, not HTML, so we don't want & → &amp; etc.
  const originalEscape = Mustache.escape
  try {
    Mustache.escape = text => text
    const result = Mustache.render(template, context).trim()

    // Return empty string if template produces only whitespace
    if (!result) {
      return ''
    }

    return result
  } finally {
    // Always restore original escape function
    Mustache.escape = originalEscape
  }
}

export const getAccountDisplayNameWithFallback = (template: MustacheTemplate, ctx: AccountNameTemplateContext) => {
  try {
    const result = formatAccountDisplayNameWithTemplate(template, ctx)
    if (!result) {
      return getDefaultAccountDisplayName(ctx)
    }
    return result
  } catch (error) {
    console.error('Failed to format account display name with template', error)
    return getDefaultAccountDisplayName(ctx)
  }
}

export const getDefaultAccountDisplayName = (ctx: AccountNameTemplateContext) =>
  `${ctx.firstName} ${ctx.lastName} - ${ctx.addressLine1}`
